import { union } from "lodash"
import { useCallback, useEffect, useState } from "react"
import { type Socket } from "socket.io-client"
import { getIntradaySocket, getOrCreateIntradaySocket } from "socket/intraday-connection"
import { PRICES_UPDATED_EVENT, type PricesUpdatedPayload } from "socket/intraday-prices"
import {
  setPriceSubscription,
  removePriceSubscriptionConsumer,
} from "socket/intraday-price-subscriptions"
import { useIntradaySocketConnection } from "./use-intraday-socket-connection"

export interface UseIntradayPriceStreamParams {
  modelProviderIds: string[]
  modelId: string
  enabled: boolean
}

export interface UseIntradayPriceStreamResult {
  prices: PricesUpdatedPayload
  updatedModelProviderIds: string[]
}

let nextPriceStreamConsumerId = 0

export function useIntradayPriceStream({
  modelProviderIds,
  modelId,
  enabled,
}: UseIntradayPriceStreamParams): UseIntradayPriceStreamResult {
  const [prices, setPrices] = useState<PricesUpdatedPayload>({})
  const [updatedModelProviderIds, setUpdatedModelProviderIds] = useState<string[]>([])

  const [previousModelId, setPreviousModelId] = useState(modelId)
  if (previousModelId !== modelId) {
    setPreviousModelId(modelId)
    setUpdatedModelProviderIds([])
  }

  const [consumerId] = useState(
    () => `intraday-price-stream-${nextPriceStreamConsumerId++}`,
  )

  const handleUnmount = useCallback((socket: Socket | undefined, unmountAccountId: string) => {
    if (socket) removePriceSubscriptionConsumer(socket, unmountAccountId, consumerId)
  }, [consumerId])

  const { accountId, registerSocketConsumer } = useIntradaySocketConnection(handleUnmount)

  useEffect(() => {
    if (!accountId) {
      return
    }

    if (!enabled || modelProviderIds.length === 0) {
      const socket = getIntradaySocket(accountId)
      if (socket) {
        removePriceSubscriptionConsumer(socket, accountId, consumerId)
      }
      return
    }

    registerSocketConsumer()
    const socket = getOrCreateIntradaySocket(accountId)

    setPriceSubscription(socket, accountId, consumerId, modelProviderIds, modelId)

    // Server sends diff-only payloads. Merge into accumulated cache so that
    // a tick containing only changed securities does not blank the rest.
    const handlePricesUpdated = (payload: PricesUpdatedPayload): void => {
      setPrices(prev => ({ ...prev, ...payload }))
      setUpdatedModelProviderIds(prev => union(prev, Object.keys(payload ?? {})))
    }

    socket.on(PRICES_UPDATED_EVENT, handlePricesUpdated)

    return () => {
      socket.off(PRICES_UPDATED_EVENT, handlePricesUpdated)
    }
  }, [enabled, accountId, modelId, modelProviderIds, registerSocketConsumer, consumerId])

  return { prices, updatedModelProviderIds }
}
